import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { apiRebuildFrontend, apiRebuildFrontendLog } from "../../services/AdminAPI";

type Phase = "confirm" | "running" | "done" | "failed";

export default function RebuildInterfaceDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const [phase, setPhase] = React.useState<Phase>("confirm");
  const [log, setLog] = React.useState("");
  const pollRef = React.useRef<any>(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  React.useEffect(() => {
    if (!open) { stopPoll(); setPhase("confirm"); setLog(""); }
    return () => stopPoll();
  }, [open]);

  const start = () => {
    setPhase("running");
    setLog("(starting…)");
    apiRebuildFrontend()
      .then(() => {
        stopPoll();
        pollRef.current = setInterval(() => {
          apiRebuildFrontendLog()
            .then((r: any) => {
              setLog(r?.data?.log ?? "");
              if (r?.data?.done) {
                stopPoll();
                setPhase(r?.data?.failed ? "failed" : "done");
              }
            })
            .catch(() => { /* keep polling */ });
        }, 3000);
      })
      .catch(() => { setPhase("failed"); setLog("Could not start the rebuild (forbidden or server error)."); });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rebuild interface</DialogTitle>
      <DialogContent>
        {phase === "confirm" ? (
          <Typography variant="body2">
            Pull the latest frontend code and rebuild it on the server
            (git pull → npm ci → npm run build). It can take a few minutes and
            affects all users on their next page reload. The backend is not restarted.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {phase === "running" ? "Building…" :
               phase === "done" ? "Build finished. Reload to load the new interface." :
               "Build failed — see the log below."}
            </Typography>
            <Box component="pre" sx={{ p: 1.25, bgcolor: "#0b1220", color: "#cfe3ff", borderRadius: 1, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 12, maxHeight: "50vh", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {log}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {phase === "confirm" && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={start}>Pull &amp; rebuild</Button>
          </>
        )}
        {phase === "running" && <Button onClick={onClose}>Close (keeps building)</Button>}
        {phase === "done" && (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={() => window.location.reload()}>Reload now</Button>
          </>
        )}
        {phase === "failed" && <Button onClick={onClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
}
